<template>
  <div class="core-page core-page--wide">
    <section
      class="card core-list-card"
      :class="{
        'core-list-card--open': isExpanded,
      }"
    >
      <button
        type="button"
        class="core-disclosure-header"
        :aria-expanded="isExpanded"
        aria-controls="liquor-order-status-content"
        @click="toggleExpanded"
      >
        <div class="core-disclosure-header-main">
          <span class="core-disclosure-title"> Your Order Status </span>

          <span class="core-disclosure-subtitle">
            — Track submitted orders and approval status.
          </span>
        </div>

        <div class="core-disclosure-header-actions">
          <!-- Waiting -->
          <span
            class="core-badge core-badge--small core-badge--warning"
            :class="{ empty: newPendingCount === 0 }"
          >
            <i class="material-icons"> schedule </i>

            <strong>{{ newPendingCount }}</strong>
            <span>New waiting</span>
          </span>

          <!-- Approved -->
          <span
            class="core-badge core-badge--small core-badge--success"
            :class="{ empty: newApprovedCount === 0 }"
          >
            <i class="material-icons"> check_circle </i>

            <strong>{{ newApprovedCount }}</strong>
            <span>New approved</span>
          </span>

          <!-- Amendments -->
          <span
            class="core-badge core-badge--small core-badge--warning"
            :class="{ empty: newAmendmentCount === 0 }"
          >
            <i class="material-icons"> edit_note </i>

            <strong>{{ newAmendmentCount }}</strong>
            <span>New amendments</span>
          </span>

          <!-- Rejected -->
          <span
            class="core-badge core-badge--small core-badge--danger"
            :class="{ empty: newRejectedCount === 0 }"
          >
            <i class="material-icons"> cancel </i>

            <strong>{{ newRejectedCount }}</strong>
            <span>New rejected</span>
          </span>

          <i
            class="material-icons core-disclosure-chevron"
            :class="{ open: isExpanded }"
            aria-hidden="true"
          >
            expand_more
          </i>
        </div>
      </button>

      <div
        v-show="isExpanded"
        id="liquor-order-status-content"
        class="core-disclosure-body"
      >
        <div class="core-stack core-stack--large">
          <!-- =================================================
               CURRENT STATUS COUNTS
               ================================================= -->

          <div class="core-inline">
            <!-- Waiting -->
            <span class="core-status-count core-status-count--warning">
              <i class="material-icons"> hourglass_top </i>

              <strong>{{ pendingCount }}</strong>

              {{
                pendingCount === 1
                  ? "order waiting for approval"
                  : "orders waiting for approval"
              }}
            </span>

            <!-- Approved -->
            <span class="core-status-count core-status-count--success">
              <i class="material-icons"> verified </i>

              <strong>{{ approvedCount }}</strong>

              {{ approvedCount === 1 ? "approved order" : "approved orders" }}
            </span>

            <!-- Amendment required -->
            <span class="core-status-count core-status-count--warning">
              <i class="material-icons"> edit_note </i>

              <strong>{{ amendmentCount }}</strong>

              {{ amendmentCount === 1 ? "amendment required" : "amendments required" }}
            </span>

            <!-- Rejected -->
            <span class="core-status-count core-status-count--danger">
              <i class="material-icons"> cancel </i>

              <strong>{{ rejectedCount }}</strong>

              {{ rejectedCount === 1 ? "rejected order" : "rejected orders" }}
            </span>
          </div>

          <div v-if="!activeVenueId" class="core-feedback core-feedback--warning">
            No venue is currently selected. Select a venue from Venue Snapshot to view its
            order status.
          </div>

          <div v-else-if="loading" class="core-loading-state">
            <div class="preloader-wrapper small active">
              <div class="spinner-layer spinner-teal-only">
                <div class="circle-clipper left">
                  <div class="circle"></div>
                </div>

                <div class="gap-patch">
                  <div class="circle"></div>
                </div>

                <div class="circle-clipper right">
                  <div class="circle"></div>
                </div>
              </div>
            </div>

            <span> Loading order status... </span>
          </div>

          <div v-else-if="errorMessage" class="core-feedback core-feedback--danger">
            {{ errorMessage }}
          </div>

          <div v-else-if="!orders.length" class="core-empty-state">
            <i class="material-icons"> receipt_long </i>

            <strong class="core-empty-state-title"> No submitted orders </strong>

            <span class="core-empty-state-copy">
              Orders submitted by this venue will appear here.
            </span>
          </div>

          <!-- =================================================
               ORDERS
               ================================================= -->

          <div v-else class="core-list-stack">
            <section
              v-for="order in orders"
              :key="order.id"
              class="core-disclosure"
              :class="[
                orderDisclosureClass(order),
                {
                  'core-disclosure--open': isOrderExpanded(order.id),
                },
              ]"
            >
              <button
                type="button"
                class="core-disclosure-header"
                :aria-expanded="isOrderExpanded(order.id)"
                :aria-controls="`liquor-order-${order.id}`"
                @click="toggleOrderExpanded(order.id)"
              >
                <div class="core-disclosure-header-main">
                  <div class="core-inline">
                    <span class="core-disclosure-title">
                      {{ order.orderReference || order.id }}
                    </span>

                    <span
                      v-if="isNewOrder(order)"
                      class="core-badge core-badge--small core-badge--info"
                    >
                      New
                    </span>
                  </div>

                  <span class="core-disclosure-subtitle">
                    {{ order.supplier || "Supplier not recorded" }}
                  </span>
                </div>

                <div class="core-disclosure-header-actions">
                  <span class="core-badge" :class="orderBadgeClass(order)">
                    <i class="material-icons">
                      {{ statusIcon(order) }}
                    </i>

                    <span>
                      {{ statusLabel(order) }}
                    </span>
                  </span>

                  <i
                    class="material-icons core-disclosure-chevron"
                    :class="{ open: isOrderExpanded(order.id) }"
                    aria-hidden="true"
                  >
                    expand_more
                  </i>
                </div>
              </button>

              <div
                v-show="isOrderExpanded(order.id)"
                :id="`liquor-order-${order.id}`"
                class="core-disclosure-body"
              >
                <div class="core-stack">
                  <div class="core-grid core-grid--4">
                    <div class="core-detail-item">
                      <span class="core-detail-label"> Submitted </span>

                      <strong class="core-detail-value">
                        {{ formatDate(order.submittedAt || order.createdAt) }}
                      </strong>
                    </div>

                    <div class="core-detail-item">
                      <span class="core-detail-label"> Order Lines </span>

                      <strong class="core-detail-value">
                        {{ orderLineCount(order) }}
                      </strong>
                    </div>

                    <div class="core-detail-item">
                      <span class="core-detail-label"> Order Units </span>

                      <strong class="core-detail-value">
                        {{ orderQuantity(order) }}
                      </strong>
                    </div>

                    <div class="core-detail-item">
                      <span class="core-detail-label"> Order Total </span>

                      <strong class="core-detail-value">
                        {{ money(orderValue(order)) }}
                      </strong>
                    </div>
                  </div>

                  <div
                    v-if="isApproved(order)"
                    class="core-callout core-callout--success"
                  >
                    <div class="core-callout-title">
                      <i class="material-icons"> verified </i>

                      <span> Approved </span>
                    </div>

                    <p v-if="approvedDate(order)">
                      Approved on {{ approvedDate(order) }}
                    </p>

                    <p v-if="approvedBy(order)">Approved by {{ approvedBy(order) }}</p>
                  </div>

                  <div
                    v-else-if="isReviewRequired(order)"
                    class="core-callout core-callout--warning"
                  >
                    <div class="core-callout-title">
                      <i class="material-icons"> edit_note </i>

                      <span> Operations has requested amendments </span>
                    </div>

                    <p class="core-note">
                      {{ decisionComments(order) }}
                    </p>

                    <p v-if="decisionBy(order)" class="core-muted">
                      Reviewed by {{ decisionBy(order) }}
                    </p>

                    <div class="core-actions">
                      <button
                        type="button"
                        class="btn amber darken-3 waves-effect waves-light"
                        @click="openResubmitModal(order)"
                      >
                        <i class="material-icons"> edit </i>

                        Edit &amp; Resubmit
                      </button>
                    </div>
                  </div>

                  <div
                    v-else-if="isRejected(order)"
                    class="core-callout core-callout--danger"
                  >
                    <div class="core-callout-title">
                      <i class="material-icons"> cancel </i>

                      <span> Order rejected </span>
                    </div>

                    <p class="core-note">
                      {{ decisionComments(order) }}
                    </p>

                    <p v-if="decisionBy(order)" class="core-muted">
                      Reviewed by {{ decisionBy(order) }}
                    </p>
                  </div>

                  <div v-else class="core-callout core-callout--warning">
                    <div class="core-callout-title">
                      <i class="material-icons"> hourglass_top </i>

                      <span> Waiting for Operations approval </span>
                    </div>

                    <p>
                      This order has been submitted and is waiting for Operations
                      approval.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>

    <!-- =====================================================
         EDIT AND RESUBMIT MODAL
         ===================================================== -->

    <div
      v-if="resubmitModalOpen"
      class="core-modal-backdrop"
      @click.self="closeResubmitModal"
    >
      <section
        class="core-modal core-modal--large core-modal--padded"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resubmit-order-title"
      >
        <div class="core-modal-header">
          <div class="core-modal-header-main">
            <h5 id="resubmit-order-title" class="core-modal-title">
              Edit &amp; Resubmit Order
            </h5>

            <p class="core-modal-subtitle">
              {{ resubmitOrder?.orderReference || resubmitOrder?.id }}
            </p>
          </div>

          <button
            type="button"
            class="core-modal-close"
            aria-label="Close resubmission form"
            :disabled="resubmitting"
            @click="closeResubmitModal"
          >
            <i class="material-icons"> close </i>
          </button>
        </div>

        <div class="core-modal-body">
          <div class="core-callout core-callout--warning">
            <span> Operations comments </span>

            <p>
              {{ decisionComments(resubmitOrder) }}
            </p>
          </div>

          <div class="core-table-wrap">
            <table class="striped core-table core-table--nowrap">
              <thead>
                <tr>
                  <th>Product</th>

                  <th>Unit Size</th>

                  <th class="right-align">Unit Cost</th>

                  <th class="center-align">Qty</th>

                  <th class="right-align">Line Total</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in resubmitItems" :key="item.id">
                  <td class="core-product-cell">
                    {{ item.product }}
                  </td>

                  <td>
                    {{ item.unitSize || "—" }}
                  </td>

                  <td class="right-align core-money-cell">
                    {{ money(item.unitPrice) }}
                  </td>

                  <td class="center-align core-quantity-cell">
                    <input
                      v-model.number="item.quantity"
                      type="number"
                      min="0"
                      step="1"
                      inputmode="numeric"
                      class="core-quantity-input"
                      :aria-label="`Resubmitted quantity for ${item.product}`"
                      :disabled="resubmitting"
                    />
                  </td>

                  <td class="right-align core-money-cell">
                    {{
                      money(cleanNumber(item.unitPrice) * cleanQuantity(item.quantity))
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="core-summary-bar">
            <span> {{ resubmitLineCount }} lines </span>

            <span> {{ resubmitQuantity }} units </span>

            <strong>
              {{ money(resubmitTotal) }}
            </strong>
          </div>

          <div class="input-field">
            <textarea
              id="resubmit-response-comments"
              v-model="resubmitResponseComments"
              class="materialize-textarea"
              maxlength="1000"
              :disabled="resubmitting"
            ></textarea>

            <label for="resubmit-response-comments" class="active">
              Response to Operations (optional)
            </label>
          </div>

          <p v-if="resubmitError" class="red-text text-darken-2">
            {{ resubmitError }}
          </p>
        </div>

        <div class="core-modal-actions">
          <button
            type="button"
            class="btn-flat"
            :disabled="resubmitting"
            @click="closeResubmitModal"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn teal darken-2 waves-effect waves-light"
            :disabled="resubmitting || resubmitLineCount === 0"
            @click="resubmitForApproval"
          >
            <i class="material-icons left"> send </i>

            {{ resubmitting ? "Resubmitting..." : "Resubmit for Approval" }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, unref, watch } from "vue";

import { collection, onSnapshot } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import { db } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

defineProps({
  currentUserProfile: {
    type: Object,
    default: () => ({}),
  },
});

const { venueId } = useVenueContext();

const functions = getFunctions(undefined, "us-central1");

const submitLiquorOrderDecisionCallable = httpsCallable(
  functions,
  "submitLiquorOrderDecision"
);

const isExpanded = ref(false);

const expandedOrderIds = ref([]);

const orders = ref([]);

const loading = ref(false);

const errorMessage = ref("");

const seenPendingOrderIds = ref([]);

const seenApprovedOrderTokens = ref([]);

const seenActionOrderTokens = ref([]);

const resubmitModalOpen = ref(false);

const resubmitOrder = ref(null);

const resubmitItems = ref([]);

const resubmitResponseComments = ref("");

const resubmitting = ref(false);

const resubmitError = ref("");

const knownStatuses = new Map();

let unsubscribeLiquorOrders = null;

let unsubscribeActiveDeliveryOrders = null;

let liquorOrderSnapshot = [];

let activeDeliveryOrderSnapshot = [];

let liquorOrdersLoaded = false;

let activeDeliveryOrdersLoaded = false;

let initialSnapshotLoaded = false;

/* =========================================================
   ACTIVE VENUE
   ========================================================= */

const activeVenueId = computed(() => {
  return String(unref(venueId) || "").trim();
});

/* =========================================================
   ORDER GROUPS
   ========================================================= */

const pendingOrders = computed(() => {
  return orders.value.filter((order) => {
    return isPending(order);
  });
});

const approvedOrders = computed(() => {
  return orders.value.filter((order) => {
    return isApproved(order);
  });
});

const amendmentOrders = computed(() => {
  return orders.value.filter((order) => {
    return isReviewRequired(order);
  });
});

const rejectedOrders = computed(() => {
  return orders.value.filter((order) => {
    return isRejected(order);
  });
});

/* =========================================================
   CURRENT COUNTS
   ========================================================= */

const pendingCount = computed(() => {
  return pendingOrders.value.length;
});

const approvedCount = computed(() => {
  return approvedOrders.value.length;
});

const amendmentCount = computed(() => {
  return amendmentOrders.value.length;
});

const rejectedCount = computed(() => {
  return rejectedOrders.value.length;
});

/* =========================================================
   NEW / UNSEEN COUNTS
   ========================================================= */

const newPendingCount = computed(() => {
  const seenIds = new Set(seenPendingOrderIds.value);

  return pendingOrders.value.filter((order) => {
    return !seenIds.has(order.id);
  }).length;
});

const newApprovedCount = computed(() => {
  const seenTokens = new Set(seenApprovedOrderTokens.value);

  return approvedOrders.value.filter((order) => {
    return !seenTokens.has(approvedToken(order));
  }).length;
});

const newAmendmentCount = computed(() => {
  const seenTokens = new Set(seenActionOrderTokens.value);

  return amendmentOrders.value.filter((order) => {
    return !seenTokens.has(actionToken(order));
  }).length;
});

const newRejectedCount = computed(() => {
  const seenTokens = new Set(seenActionOrderTokens.value);

  return rejectedOrders.value.filter((order) => {
    return !seenTokens.has(actionToken(order));
  }).length;
});

/* =========================================================
   RESUBMISSION TOTALS
   ========================================================= */

const resubmitLineCount = computed(() => {
  return resubmitItems.value.filter((item) => {
    return cleanQuantity(item.quantity) > 0;
  }).length;
});

const resubmitQuantity = computed(() => {
  return resubmitItems.value.reduce((total, item) => {
    return total + cleanQuantity(item.quantity);
  }, 0);
});

const resubmitTotal = computed(() => {
  return resubmitItems.value.reduce((total, item) => {
    return total + cleanNumber(item.unitPrice) * cleanQuantity(item.quantity);
  }, 0);
});

/* =========================================================
   VENUE WATCH
   ========================================================= */

watch(
  activeVenueId,

  () => {
    isExpanded.value = false;

    expandedOrderIds.value = [];

    closeResubmitModal();

    loadSeenState();

    subscribeToOrders();
  },

  {
    immediate: true,
  }
);

onBeforeUnmount(() => {
  if (isExpanded.value) {
    markCurrentOrdersAsSeen();
  }

  unsubscribeLiquorOrders?.();

  unsubscribeActiveDeliveryOrders?.();

  unsubscribeLiquorOrders = null;

  unsubscribeActiveDeliveryOrders = null;
});

/* =========================================================
   COLLAPSIBLE
   ========================================================= */

function toggleExpanded() {
  if (isExpanded.value) {
    markCurrentOrdersAsSeen();
  }

  isExpanded.value = !isExpanded.value;
}

function isOrderExpanded(orderId) {
  return expandedOrderIds.value.includes(orderId);
}

function toggleOrderExpanded(orderId) {
  if (isOrderExpanded(orderId)) {
    expandedOrderIds.value = expandedOrderIds.value.filter((id) => id !== orderId);

    return;
  }

  expandedOrderIds.value = [...expandedOrderIds.value, orderId];
}

/* =========================================================
   FIRESTORE ORDERS
   ========================================================= */

function subscribeToOrders() {
  unsubscribeLiquorOrders?.();

  unsubscribeActiveDeliveryOrders?.();

  unsubscribeLiquorOrders = null;

  unsubscribeActiveDeliveryOrders = null;

  liquorOrderSnapshot = [];

  activeDeliveryOrderSnapshot = [];

  liquorOrdersLoaded = false;

  activeDeliveryOrdersLoaded = false;

  orders.value = [];

  errorMessage.value = "";

  knownStatuses.clear();

  initialSnapshotLoaded = false;

  if (!activeVenueId.value) {
    loading.value = false;

    return;
  }

  loading.value = true;

  unsubscribeLiquorOrders = onSnapshot(
    collection(db, "venues", activeVenueId.value, "liquor-orders"),

    (snapshot) => {
      liquorOrderSnapshot = snapshot.docs.map((orderDocument) => ({
        id: orderDocument.id,

        ...orderDocument.data(),
      }));

      liquorOrdersLoaded = true;

      rebuildOrders();
    },

    (error) => {
      console.error(
        "[LiquorOrderStatus] Liquor order status load failed:",
        error
      );

      liquorOrderSnapshot = [];

      liquorOrdersLoaded = true;

      errorMessage.value =
        error?.message || "Liquor order statuses could not be loaded.";

      rebuildOrders();
    }
  );

  unsubscribeActiveDeliveryOrders = onSnapshot(
    collection(db, "venues", activeVenueId.value, "active_delivery_orders"),

    (snapshot) => {
      activeDeliveryOrderSnapshot = snapshot.docs.map((orderDocument) => ({
        id: orderDocument.id,

        ...orderDocument.data(),
      }));

      activeDeliveryOrdersLoaded = true;

      rebuildOrders();
    },

    (error) => {
      console.error(
        "[LiquorOrderStatus] Approved delivery order load failed:",
        error
      );

      activeDeliveryOrderSnapshot = [];

      activeDeliveryOrdersLoaded = true;

      errorMessage.value =
        error?.message || "Approved orders awaiting delivery could not be loaded.";

      rebuildOrders();
    }
  );
}

function rebuildOrders() {
  if (!liquorOrdersLoaded || !activeDeliveryOrdersLoaded) {
    loading.value = true;

    return;
  }

  const mergedOrders = new Map();

  liquorOrderSnapshot
    .filter((order) => {
      return isPending(order) || isReviewRequired(order) || isRejected(order);
    })
    .forEach((order) => {
      mergedOrders.set(order.id, order);
    });

  activeDeliveryOrderSnapshot
    .filter((order) => {
      return isApproved(order);
    })
    .forEach((order) => {
      /*
       * Approved orders live in active_delivery_orders until the
       * delivery note is saved. If the same ID is momentarily present
       * in both snapshots, the approved active-delivery version wins.
       */
      mergedOrders.set(order.id, order);
    });

  const nextOrders = Array.from(mergedOrders.values()).sort((a, b) => {
    return latestOrderTimestamp(b) - latestOrderTimestamp(a);
  });

  if (initialSnapshotLoaded) {
    notifyStatusChanges(nextOrders);
  } else {
    notifyStatusesOnLoad(nextOrders);
  }

  nextOrders.forEach((order) => {
    knownStatuses.set(order.id, normalisedStatus(order));
  });

  orders.value = nextOrders;

  expandedOrderIds.value = expandedOrderIds.value.filter((orderId) =>
    nextOrders.some((order) => order.id === orderId)
  );

  loading.value = false;

  initialSnapshotLoaded = true;
}

/* =========================================================
   NOTIFICATIONS

   RAG:
   Green = approved
   Amber = amendment required
   Red = rejected
   ========================================================= */

function notifyStatusesOnLoad(nextOrders) {
  const seenApprovedTokens = new Set(seenApprovedOrderTokens.value);

  const seenActionTokens = new Set(seenActionOrderTokens.value);

  const unseenApprovedOrders = nextOrders.filter((order) => {
    return isApproved(order) && !seenApprovedTokens.has(approvedToken(order));
  });

  const unseenAmendmentOrders = nextOrders.filter((order) => {
    return isReviewRequired(order) && !seenActionTokens.has(actionToken(order));
  });

  const unseenRejectedOrders = nextOrders.filter((order) => {
    return isRejected(order) && !seenActionTokens.has(actionToken(order));
  });

  if (unseenApprovedOrders.length) {
    const message =
      unseenApprovedOrders.length === 1
        ? `${
            unseenApprovedOrders[0].orderReference || "Your liquor order"
          } has been approved.`
        : `${unseenApprovedOrders.length} liquor orders have been approved.`;

    showToast(message, "green darken-2");
  }

  if (unseenAmendmentOrders.length) {
    const message =
      unseenAmendmentOrders.length === 1
        ? `${
            unseenAmendmentOrders[0].orderReference || "Your liquor order"
          } requires amendments.`
        : `${unseenAmendmentOrders.length} liquor orders require amendments.`;

    showToast(message, "amber darken-3");
  }

  if (unseenRejectedOrders.length) {
    const message =
      unseenRejectedOrders.length === 1
        ? `${unseenRejectedOrders[0].orderReference || "Your liquor order"} was rejected.`
        : `${unseenRejectedOrders.length} liquor orders were rejected.`;

    showToast(message, "red darken-2");
  }
}

function notifyStatusChanges(nextOrders) {
  nextOrders.forEach((order) => {
    const previousStatus = knownStatuses.get(order.id);

    const currentStatus = normalisedStatus(order);

    if (previousStatus && previousStatus !== "approved" && currentStatus === "approved") {
      const reference = order.orderReference || "Your liquor order";

      showToast(`${reference} has been approved.`, "green darken-2");
    }

    if (
      previousStatus &&
      previousStatus !== currentStatus &&
      currentStatus === "review_required"
    ) {
      showToast(
        `${order.orderReference || "Your liquor order"} requires amendments.`,

        "amber darken-3"
      );
    }

    if (
      previousStatus &&
      previousStatus !== currentStatus &&
      currentStatus === "rejected"
    ) {
      showToast(
        `${order.orderReference || "Your liquor order"} was rejected.`,

        "red darken-2"
      );
    }
  });
}

/* =========================================================
   SEEN STATE
   ========================================================= */

function markCurrentOrdersAsSeen() {
  if (!activeVenueId.value) {
    return;
  }

  seenPendingOrderIds.value = pendingOrders.value.map((order) => {
    return order.id;
  });

  seenApprovedOrderTokens.value = approvedOrders.value.map((order) => {
    return approvedToken(order);
  });

  /*
   * Amendment and rejected orders share the existing
   * action-token storage, but are counted separately
   * for RAG display.
   */
  seenActionOrderTokens.value = [...amendmentOrders.value, ...rejectedOrders.value].map(
    (order) => {
      return actionToken(order);
    }
  );

  saveSeenState();
}

function approvedToken(order) {
  const approvedTime = timestampMillis(
    order.approvedAt || order.decisionAt || order.updatedAt
  );

  return `${order.id}:${approvedTime || "approved"}`;
}

function actionToken(order) {
  const decisionTime = timestampMillis(order.decisionAt || order.updatedAt);

  return `${order.id}:${normalisedStatus(order)}:${decisionTime || "decision"}`;
}

function storageKey() {
  return `coredeck-liquor-order-status:` + activeVenueId.value;
}

function loadSeenState() {
  seenPendingOrderIds.value = [];

  seenApprovedOrderTokens.value = [];

  seenActionOrderTokens.value = [];

  if (!activeVenueId.value) {
    return;
  }

  try {
    const storedValue = window.localStorage.getItem(storageKey());

    if (!storedValue) {
      return;
    }

    const parsedValue = JSON.parse(storedValue);

    seenPendingOrderIds.value = Array.isArray(parsedValue?.pending)
      ? parsedValue.pending
      : [];

    seenApprovedOrderTokens.value = Array.isArray(parsedValue?.approved)
      ? parsedValue.approved
      : [];

    seenActionOrderTokens.value = Array.isArray(parsedValue?.actions)
      ? parsedValue.actions
      : [];
  } catch (error) {
    console.warn("[LiquorOrderStatus] Seen status could not be loaded:", error);

    seenPendingOrderIds.value = [];

    seenApprovedOrderTokens.value = [];

    seenActionOrderTokens.value = [];
  }
}

function saveSeenState() {
  if (!activeVenueId.value) {
    return;
  }

  try {
    window.localStorage.setItem(
      storageKey(),

      JSON.stringify({
        pending: seenPendingOrderIds.value,

        approved: seenApprovedOrderTokens.value,

        actions: seenActionOrderTokens.value,
      })
    );
  } catch (error) {
    console.warn("[LiquorOrderStatus] Seen status could not be saved:", error);
  }
}

function isNewOrder(order) {
  if (isApproved(order)) {
    return !seenApprovedOrderTokens.value.includes(approvedToken(order));
  }

  if (isReviewRequired(order) || isRejected(order)) {
    return !seenActionOrderTokens.value.includes(actionToken(order));
  }

  return !seenPendingOrderIds.value.includes(order.id);
}

/* =========================================================
   ORDER STATUS
   ========================================================= */

function normalisedStatus(order) {
  const approvalStatus = String(order?.approvalStatus || "")
    .trim()
    .toLowerCase();

  const status = String(order?.status || "")
    .trim()
    .toLowerCase();

  /*
   * APPROVED = GREEN
   */
  if (
    approvalStatus === "approved" ||
    approvalStatus === "approved_amended" ||
    status === "approved" ||
    status === "approved_amended" ||
    status === "awaiting_delivery" ||
    status === "ready_for_delivery" ||
    status === "delivery_note_created"
  ) {
    return "approved";
  }

  /*
   * AMENDMENT REQUIRED = AMBER
   */
  if (
    approvalStatus === "review_required" ||
    status === "amendment_required" ||
    String(order?.reviewStatus || "")
      .trim()
      .toLowerCase() === "review required"
  ) {
    return "review_required";
  }

  /*
   * REJECTED = RED
   */
  if (approvalStatus === "rejected" || status === "rejected") {
    return "rejected";
  }

  /*
   * PENDING = AMBER
   */
  return "pending";
}

function orderDisclosureClass(order) {
  if (isApproved(order)) {
    return "core-disclosure--success";
  }

  if (isRejected(order)) {
    return "core-disclosure--danger";
  }

  return "core-disclosure--warning";
}

function orderBadgeClass(order) {
  if (isApproved(order)) {
    return "core-badge--success";
  }

  if (isRejected(order)) {
    return "core-badge--danger";
  }

  return "core-badge--warning";
}

function isApproved(order) {
  return normalisedStatus(order) === "approved";
}

function isReviewRequired(order) {
  return normalisedStatus(order) === "review_required";
}

function isRejected(order) {
  return normalisedStatus(order) === "rejected";
}

function isPending(order) {
  const approvalStatus = String(order?.approvalStatus || "")
    .trim()
    .toLowerCase();

  const status = String(order?.status || "")
    .trim()
    .toLowerCase();

  return (
    approvalStatus === "pending" ||
    status === "pending" ||
    status === "pending_approval" ||
    (!approvalStatus && !status)
  );
}

function wasAmended(order) {
  const approvalStatus = String(order?.approvalStatus || "")
    .trim()
    .toLowerCase();

  const status = String(order?.status || "")
    .trim()
    .toLowerCase();

  return Boolean(
    order?.wasAmended ||
      order?.amended ||
      approvalStatus === "approved_amended" ||
      status === "approved_amended"
  );
}

function statusLabel(order) {
  if (isApproved(order)) {
    return wasAmended(order) ? "Approved with Amendments" : "Approved";
  }

  if (isReviewRequired(order)) {
    return "Amendment Required";
  }

  if (isRejected(order)) {
    return "Rejected";
  }

  return "Waiting for Approval";
}

function statusIcon(order) {
  if (isApproved(order)) {
    return "check_circle";
  }

  if (isReviewRequired(order)) {
    return "edit_note";
  }

  if (isRejected(order)) {
    return "cancel";
  }

  return "schedule";
}

/* =========================================================
   ORDER VALUES
   ========================================================= */

function orderItems(order) {
  if (isApproved(order) && Array.isArray(order.approvedItems)) {
    return order.approvedItems;
  }

  if (isReviewRequired(order) && Array.isArray(order.requestedAmendmentItems)) {
    return order.requestedAmendmentItems;
  }

  return Array.isArray(order.items) ? order.items : [];
}

function orderLineCount(order) {
  return orderItems(order).length;
}

function orderQuantity(order) {
  const storedTotal = isApproved(order)
    ? order.approvedTotals?.totalQuantity ?? order.totals?.totalQuantity
    : isReviewRequired(order)
    ? null
    : order.totals?.totalQuantity;

  if (Number.isFinite(Number(storedTotal))) {
    return Number(storedTotal);
  }

  return orderItems(order).reduce((total, item) => {
    return total + cleanNumber(item.quantity);
  }, 0);
}

function orderValue(order) {
  const storedTotal = isApproved(order)
    ? order.approvedTotals?.orderTotal ?? order.totals?.orderTotal
    : isReviewRequired(order)
    ? null
    : order.totals?.orderTotal;

  if (Number.isFinite(Number(storedTotal))) {
    return Number(storedTotal);
  }

  return orderItems(order).reduce((total, item) => {
    return total + cleanNumber(item.unitPrice) * cleanNumber(item.quantity);
  }, 0);
}

/* =========================================================
   APPROVAL / DECISION DETAILS
   ========================================================= */

function approvedDate(order) {
  const value = order.approvedAt || order.decisionAt || order.updatedAt;

  if (!timestampMillis(value)) {
    return "";
  }

  return formatDate(value);
}

function approvedBy(order) {
  return String(
    order.approvedByName ||
      order.approvedByEmail ||
      order.decisionByName ||
      order.decisionByEmail ||
      ""
  ).trim();
}

function decisionComments(order) {
  return String(
    order?.decisionComments ||
      order?.finalApproval?.approvalComments ||
      "No comments were supplied."
  ).trim();
}

function decisionBy(order) {
  return String(
    order?.decisionByName ||
      order?.decisionByEmail ||
      order?.finalApproval?.approvedBy ||
      ""
  ).trim();
}

/* =========================================================
   RESUBMIT ORDER
   ========================================================= */

function cleanQuantity(value) {
  return Math.max(
    0,

    Math.floor(cleanNumber(value))
  );
}

function openResubmitModal(order) {
  const sourceItems = Array.isArray(order?.requestedAmendmentItems)
    ? order.requestedAmendmentItems
    : Array.isArray(order?.items)
    ? order.items
    : [];

  resubmitOrder.value = order;

  resubmitItems.value = sourceItems.map((item) => ({
    ...item,

    quantity: cleanQuantity(item.quantity),
  }));

  resubmitResponseComments.value = "";

  resubmitError.value = "";

  resubmitModalOpen.value = true;

  nextTick(() => {
    window.M?.updateTextFields?.();
  });
}

function closeResubmitModal() {
  if (resubmitting.value) {
    return;
  }

  resubmitModalOpen.value = false;

  resubmitOrder.value = null;

  resubmitItems.value = [];

  resubmitResponseComments.value = "";

  resubmitError.value = "";
}

async function resubmitForApproval() {
  if (!resubmitOrder.value || resubmitting.value || !resubmitLineCount.value) {
    return;
  }

  resubmitting.value = true;

  resubmitError.value = "";

  try {
    await submitLiquorOrderDecisionCallable({
      venueId: activeVenueId.value,

      orderId: resubmitOrder.value.id,

      decision: "Resubmit",

      responseComments: resubmitResponseComments.value.trim(),

      items: resubmitItems.value
        .map((item) => ({
          id: item.id,

          quantity: cleanQuantity(item.quantity),
        }))
        .filter((item) => {
          return item.quantity > 0;
        }),
    });

    showToast(
      `${resubmitOrder.value.orderReference || "Liquor order"} resubmitted for approval.`,

      "green darken-2"
    );

    resubmitModalOpen.value = false;

    resubmitOrder.value = null;

    resubmitItems.value = [];

    resubmitResponseComments.value = "";
  } catch (error) {
    console.error("[LiquorOrderStatus] Resubmission failed:", error);

    resubmitError.value =
      error?.details?.message || error?.message || "This order could not be resubmitted.";
  } finally {
    resubmitting.value = false;
  }
}

/* =========================================================
   DATE / NUMBER HELPERS
   ========================================================= */

function latestOrderTimestamp(order) {
  return timestampMillis(
    order.decisionAt ||
      order.approvedAt ||
      order.updatedAt ||
      order.resubmittedAt ||
      order.submittedAt ||
      order.createdAt
  );
}

function timestampMillis(value) {
  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (value?.seconds) {
    return Number(value.seconds) * 1000;
  }

  const parsed = new Date(value || 0).getTime();

  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDate(value) {
  const millis = timestampMillis(value);

  if (!millis) {
    return "Date pending";
  }

  return new Intl.DateTimeFormat(
    "en-GB",

    {
      dateStyle: "medium",

      timeStyle: "short",
    }
  ).format(new Date(millis));
}

function cleanNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}

function money(value) {
  return new Intl.NumberFormat(
    "en-GB",

    {
      style: "currency",

      currency: "GBP",
    }
  ).format(cleanNumber(value));
}

function showToast(message, classes = "") {
  window.M?.toast?.({
    html: message,

    classes,
  });
}
</script>
